// src/pages/Inventario.js
import React, { useEffect, useMemo, useState } from "react";
import {
  collectionGroup,
  deleteDoc,
  doc,
  increment,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { parseQrPayload } from "../utils/qr"; // se mantiene para lectura de QR
import ScannerQR from "../components/ScannerQR";

const toTitle = (s = "") =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

/** Descargar QR (autónomo, sin tocar utils) */
async function downloadItemQR(item) {
  try {
    // QR con el mismo formato que espera tu parseQrPayload: JSON
    const payload = JSON.stringify({
      id: item.id,
      nombre: item.nombre,
      almacen: item.ubicacion,
    });

    const QR = await import("qrcode");
    const dataUrl = await QR.toDataURL(payload, { margin: 2, width: 384 });

    const a = document.createElement("a");
    const safeName = String(item.nombre || "utensilio")
      .trim()
      .replace(/\s+/g, "_")
      .slice(0, 40);
    a.href = dataUrl;
    a.download = `QR_${item.ubicacion}_${safeName}_${item.id}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    console.error("Error generando/descargando QR:", e);
    alert("No se pudo descargar el QR.");
  }
}

export default function Inventario() {
  // ====== Estado base ======
  const [items, setItems] = useState([]); // {id, nombre, cantidad, ubicacion, notas, path}
  const [search, setSearch] = useState("");
  const [filtroUbicacion, setFiltroUbicacion] = useState("Todos");

  // ====== Estado para editar ======
  const [editId, setEditId] = useState(null);
  const [editNombre, setEditNombre] = useState("");
  const [editCantidad, setEditCantidad] = useState(0);
  const [editNotas, setEditNotas] = useState("");
  const [editUbicacion, setEditUbicacion] = useState("");

  // ====== Estado del modal del escáner ======
  const [scanOpen, setScanOpen] = useState(false);

  // ====== Suscripción a todos los items ======
  useEffect(() => {
    const q = query(collectionGroup(db, "items"), orderBy("nombre"));
    const unsub = onSnapshot(q, (snap) => {
      const rows = [];
      snap.forEach((d) => {
        const data = d.data() || {};
        rows.push({
          id: d.id,
          nombre: data.nombre || "",
          cantidad: typeof data.cantidad === "number" ? data.cantidad : 0,
          ubicacion: data.ubicacion || "", // "estadio", "rambla", "torrecardenas"
          notas: data.notas || "",
          path: d.ref.path,
        });
      });
      setItems(rows);
    });
    return () => unsub();
  }, []);

  // ====== Helpers ======
  const getDocRefFromPath = (path) => doc(db, path);

  const inc = async (it, delta) => {
    const ref = getDocRefFromPath(it.path);
    await updateDoc(ref, { cantidad: increment(delta) });
  };

  const removeItem = async (it) => {
    const ok = window.confirm(
      `¿Eliminar "${it.nombre}" (${toTitle(it.ubicacion)})?`
    );
    if (!ok) return;
    await deleteDoc(getDocRefFromPath(it.path));
  };

  const openEdit = (it) => {
    setEditId(it.id);
    setEditNombre(it.nombre);
    setEditCantidad(it.cantidad);
    setEditNotas(it.notas || "");
    setEditUbicacion(it.ubicacion);
  };

  const saveEdit = async () => {
    const it = items.find((x) => x.id === editId);
    if (!it) {
      setEditId(null);
      return;
    }
    await updateDoc(getDocRefFromPath(it.path), {
      nombre: editNombre.trim(),
      cantidad: Number(editCantidad) || 0,
      notas: (editNotas || "").trim(),
    });
    setEditId(null);
  };

  // ====== Filtro/Busqueda ======
  const filtered = useMemo(() => {
    const t = (search || "").trim().toLowerCase();
    return items.filter((it) => {
      const passUb =
        filtroUbicacion === "Todos" ||
        it.ubicacion.toLowerCase() === filtroUbicacion.toLowerCase();
      if (!passUb) return false;
      if (!t) return true;
      return (
        it.nombre.toLowerCase().includes(t) ||
        (it.notas || "").toLowerCase().includes(t) ||
        it.ubicacion.toLowerCase().includes(t)
      );
    });
  }, [items, search, filtroUbicacion]);

  // ====== Manejo del QR decodificado ======
  const handleScanDecoded = (decodedText) => {
    try {
      const payload = parseQrPayload(decodedText); // { id, almacen?, ... }
      if (!payload || !payload.id) {
        alert("QR no válido.");
        setScanOpen(false);
        return;
      }
      const found = items.find((x) => x.id === payload.id);
      if (!found) {
        alert("El QR no corresponde a ningún utensilio visible.");
        setScanOpen(false);
        return;
      }
      openEdit(found);
    } catch (e) {
      console.error(e);
      alert("QR no válido.");
    } finally {
      setScanOpen(false);
    }
  };

  // ====== Render ======
  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Nombre, notas o ubicación…"
          className="flex-1 px-3 py-2 rounded border"
        />

        <select
          value={filtroUbicacion}
          onChange={(e) => setFiltroUbicacion(e.target.value)}
          className="px-3 py-2 rounded border"
        >
          <option value="Todos">Todos</option>
          <option value="estadio">Estadio</option>
          <option value="rambla">Rambla</option>
          <option value="torrecardenas">Torrecardenas</option>
        </select>

        <button
          onClick={() => setScanOpen(true)}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white shadow"
        >
          Escanear QR
        </button>
      </div>

      <div className="bg-white/90 backdrop-blur rounded-xl shadow p-3">
        <table className="min-w-full">
          <thead>
            <tr className="text-left text-slate-600">
              <th className="px-3 py-2">Nombre</th>
              <th className="px-3 py-2">Cantidad</th>
              <th className="px-3 py-2">Ubicación</th>
              <th className="px-3 py-2">Notas</th>
              <th className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} className="border-t">
                <td className="px-3 py-2">{it.nombre}</td>
                <td className="px-3 py-2">{it.cantidad}</td>
                <td className="px-3 py-2">{toTitle(it.ubicacion)}</td>

                {/* Notas SIEMPRE con botón Editar */}
                <td className="px-3 py-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    {it.notas ? (
                      <span className="text-slate-800">{it.notas}</span>
                    ) : (
                      <span className="text-slate-400 italic">—</span>
                    )}
                    <button
                      onClick={() => openEdit(it)}
                      className="px-3 py-1 rounded border text-slate-700 hover:bg-slate-50"
                    >
                      Editar
                    </button>
                  </div>
                </td>

                <td className="px-3 py-2">
                  <div className="flex gap-2 justify-end">
                    <button
                      className="px-2 py-1 rounded border hover:bg-slate-50"
                      onClick={() => inc(it, -1)}
                      title="Restar 1"
                    >
                      −
                    </button>
                    <button
                      className="px-2 py-1 rounded border hover:bg-slate-50"
                      onClick={() => inc(it, +1)}
                      title="Sumar 1"
                    >
                      +
                    </button>

                    {/* Botón QR para descargar el QR del utensilio */}
                    <button
                      className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700"
                      onClick={() => downloadItemQR(it)}
                      title="Descargar QR"
                    >
                      QR
                    </button>

                    <button
                      className="px-2 py-1 rounded bg-red-500 text-white hover:bg-red-600"
                      onClick={() => removeItem(it)}
                      title="Eliminar"
                    >
                      🗑
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-slate-500">
                  Sin resultados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ===== Modal de edición ===== */}
      {editId && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                Editar: <span className="font-normal">{editNombre}</span>{" "}
                <span className="text-slate-500">
                  ({toTitle(editUbicacion)})
                </span>
              </h3>
              <button
                onClick={() => setEditId(null)}
                className="text-slate-600 hover:text-slate-900"
              >
                Cerrar
              </button>
            </div>
            <div className="p-4 grid gap-3">
              <div>
                <label className="block text-sm text-slate-600 mb-1">
                  Nombre
                </label>
                <input
                  value={editNombre}
                  onChange={(e) => setEditNombre(e.target.value)}
                  className="w-full px-3 py-2 rounded border"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-600 mb-1">
                  Cantidad
                </label>
                <input
                  type="number"
                  value={editCantidad}
                  onChange={(e) => setEditCantidad(e.target.value)}
                  className="w-full px-3 py-2 rounded border"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-600 mb-1">Notas</label>
                <textarea
                  value={editNotas}
                  onChange={(e) => setEditNotas(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 rounded border"
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => setEditId(null)}
                className="px-4 py-2 rounded border hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={saveEdit}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Modal del escáner ===== */}
      {scanOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Escanear QR</h3>
              <button
                onClick={() => setScanOpen(false)}
                className="text-slate-600 hover:text-slate-900"
              >
                Cerrar
              </button>
            </div>
            <div className="p-4">
              <ScannerQR onDecoded={handleScanDecoded} onClose={() => {}} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
